import Alert from '@mui/material/Alert';
import React, { Reducer, useCallback, useEffect, useReducer, useState } from 'react';
import Table from 'components/shared/Table';
import { EMPTY_STRING_VALUE, getEmailLink, getUserFullName } from 'util/Format';
import { SortConfig, TableColumn } from 'store/types/Table';
import { userFullNameSortLabel } from 'util/Table';
import { defaultGridContainerProps, defaultGridItemProps, defaultSnackbarErrorProps } from 'util/Layout';
import CompanyUser from 'store/types/CompanyUser';
import UseRequestData from 'store/types/UseRequestData';
import { Grid, Button, Chip, Checkbox, Skeleton } from '@mui/material';
import CompanyService from 'services/api/CompanyService';
import { useSnackbar } from 'notistack';
import reducer, {
  initialState,
  CompanyUsersTableAction,
  CompanyUsersTableActionType,
  CompanyUsersTableState,
  TableCompanyUsers,
} from './CompanyUsersTableReducer';
import JobFunction from 'store/types/JobFunction';
import ViewCompanyUserModal from 'components/company/CompanyUsersSection/ViewCompanyUserModal';
import { MemberClassification } from 'store/types/Company';
import SelectOption from 'store/types/SelectOption';
import { UserEmployerAccessRequestsData } from 'store/types/UserEmployerAccessRequest';
import { isPendingRequestToCompanyExist } from 'util/Company';

import styles from './CompanyUsersTable.module.scss';
import commonStyles from 'styles/common.module.scss';

interface CompanyUsersTableProps extends UseRequestData<CompanyUser[]> {
  companyId: string;
  jobFunctionsList: JobFunction[];
  companyRequests?: UserEmployerAccessRequestsData;
  memberClassification?: MemberClassification[];
  directoryStatusOptions?: SelectOption[];
}

const CompanyUsersTable: React.FunctionComponent<CompanyUsersTableProps> = ({
  companyId,
  data,
  loading,
  error,
  refetch,
  jobFunctionsList,
  companyRequests,
  memberClassification,
  directoryStatusOptions,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loadingEmployeeUpdate, setLoadingEmployeeUpdate] = useState<boolean>(false);
  const [{ sort, list = [], selectedUser }, dispatch] = useReducer<
    Reducer<CompanyUsersTableState, CompanyUsersTableAction>
  >(reducer, initialState);

  useEffect(() => {
    if (data) {
      dispatch({
        type: CompanyUsersTableActionType.SetInitialList,
        payload: { initialList: data },
      });
    }
  }, [data, companyId]);

  const handleViewModalOpen = useCallback(
    (record) => () => {
      dispatch({
        type: CompanyUsersTableActionType.OpenViewUserModal,
        payload: { selectedUser: record },
      });
    },
    []
  );

  const handleViewModalClose = useCallback(() => {
    dispatch({ type: CompanyUsersTableActionType.CloseViewUserModal, payload: {} });
  }, []);

  const handleSortChange = useCallback((newSort: SortConfig) => {
    dispatch({
      type: CompanyUsersTableActionType.UpdateSort,
      payload: { sort: newSort },
    });
  }, []);

  // const handleReceiveEmailsClick = useCallback(
  //   (record) => () => {
  //     setLoadingReceiveEmails(true);
  //     const { receiveEmails, id } = record;
  //     CompanyService.updateReceiveEmailsFlag(companyId, id, !receiveEmails)
  //       .then(() => {
  //         enqueueSnackbar('Receive Emails successfully updated', { variant: 'success' });
  //         refetch();
  //         setLoadingReceiveEmails(false);
  //       })
  //       .catch((errorMessage) => {
  //         enqueueSnackbar(errorMessage, defaultSnackbarErrorProps);
  //         setLoadingReceiveEmails(false);
  //       });
  //   },
  //   [companyId, enqueueSnackbar, refetch]
  // );

  const handleEmployeeToggleClick = useCallback(
    (record) => () => {
      const { isEmployee, id } = record;

      setLoadingEmployeeUpdate(true);
      if (isEmployee) {
        CompanyService.removeCompanyEmployee(companyId, id)
          .then(() => {
            enqueueSnackbar('Employeer request created', { variant: 'success' });
          })
          .catch((errorMessage) => {
            enqueueSnackbar(errorMessage, defaultSnackbarErrorProps);
          })
          .finally(() => {
            setLoadingEmployeeUpdate(false);
            refetch();
          });
      } else {
        CompanyService.linkCompanyEmployee(companyId, id)
          .then(() => {
            enqueueSnackbar('Employee status successfully updated', { variant: 'success' });
          })
          .catch((errorMessage) => {
            enqueueSnackbar(errorMessage, defaultSnackbarErrorProps);
          })
          .finally(() => {
            setLoadingEmployeeUpdate(false);
            refetch();
          });
      }
    },
    [companyId, enqueueSnackbar, refetch]
  );

  const columns: Array<TableColumn<TableCompanyUsers>> = [
    {
      dataIndex: 'isEmployee',
      label: 'Employee',
      sortable: true,
      render: (isEmployee: boolean, record: TableCompanyUsers) => {
        const pending = companyRequests && isPendingRequestToCompanyExist(record.id, companyRequests);

        return loadingEmployeeUpdate ? (
          <Skeleton variant={'rectangular'} width={45} />
        ) : (
          <Checkbox
            color={'primary'}
            checked={isEmployee}
            indeterminate={pending}
            disabled={pending}
            onClick={handleEmployeeToggleClick(record)}
          />
        );
      },
    },
    {
      dataIndex: userFullNameSortLabel,
      label: 'Name',
      sortable: true,
      render: (_: any, record: TableCompanyUsers) => getUserFullName(record, true),
    },
    {
      dataIndex: 'title',
      label: 'Job title',
      sortable: true,
      render: (title: string) => (title ? title : EMPTY_STRING_VALUE),
    },
    {
      dataIndex: 'email',
      label: 'Email',
      sortable: true,
      render: (email: string) => (email ? getEmailLink(email) : EMPTY_STRING_VALUE),
    },
    {
      dataIndex: 'functions',
      label: 'Portal Role',
      sortable: true,
      render: (functions: CompanyUser['functions']) =>
        functions?.length
          ? functions.map((func) => (
              <Chip
                sx={{ marginRight: '0.5rem' }}
                key={`function-${func.id}`}
                label={func.functionName}
                color={'secondary'}
                className={styles.functionChip}
              />
            ))
          : null,
    },
    {
      label: 'Action',
      align: 'center',
      render: (_: any, record: TableCompanyUsers) => (
        <Button size={'small'} onClick={handleViewModalOpen(record)} variant={'outlined'}>
          {'Edit'}
        </Button>
      ),
    },
  ];

  return (
    <>
      <Grid {...defaultGridContainerProps}>
        {error ? (
          <Grid {...defaultGridItemProps}>
            <Alert severity={'error'} className={commonStyles.alert}>
              {error}
            </Alert>
          </Grid>
        ) : (
          <Grid {...defaultGridItemProps}>
            <Table
              columns={columns}
              list={list}
              sort={sort}
              onSortChange={handleSortChange}
              loading={loading}
              showPagination={true}
            />
          </Grid>
        )}
      </Grid>
      {selectedUser && (
        <ViewCompanyUserModal
          open={!!selectedUser}
          onClose={handleViewModalClose}
          data={selectedUser}
          jobFunctionsList={jobFunctionsList}
          refetch={refetch}
          companyId={companyId}
          memberClassification={memberClassification}
          directoryStatusOptions={directoryStatusOptions}
        />
      )}
    </>
  );
};
export default CompanyUsersTable;
